import { useEffect, useRef, useState } from 'react';

const proyectos = [
  { icon: 'ri-road-map-line', numero: 150, prefix: '+', label: 'Proyectos ejecutados' },
  { icon: 'ri-building-2-line', numero: 20, prefix: '+', label: 'Años de experiencia' },
  { icon: 'ri-group-line', numero: 80, prefix: '+', label: 'Clientes satisfechos' },
  { icon: 'ri-map-pin-2-line', numero: 10, prefix: '+', label: 'Estados de la República' },
];

const categorias = [
  {
    icon: 'ri-road-map-line',
    titulo: 'Infraestructura Vial',
    descripcion:
      'Construcción y rehabilitación de carreteras, caminos rurales, puentes y obras de drenaje que conectan comunidades y mejoran la movilidad regional.',
    imagen:
      'https://readdy.ai/api/search-image?query=aerial%20view%20of%20modern%20highway%20infrastructure%20construction%20in%20Mexico%2C%20asphalt%20road%20with%20lane%20markings%2C%20bridges%20and%20overpasses%2C%20construction%20machinery%20working%2C%20warm%20golden%20hour%20lighting%2C%20professional%20photography%2C%20clean%20minimalist%20background&width=600&height=400&seq=proyecto-vial-001&orientation=landscape',
  },
  {
    icon: 'ri-building-4-line',
    titulo: 'Obra Civil',
    descripcion:
      'Desarrollo de edificaciones, estructuras de concreto, cimentaciones y obras complementarias con altos estándares de calidad y seguridad.',
    imagen:
      'https://readdy.ai/api/search-image?query=civil%20construction%20project%20concrete%20building%20structure%20with%20steel%20reinforcement%2C%20workers%20in%20hard%20hats%2C%20modern%20urban%20development%20Mexico%2C%20professional%20corporate%20photography%2C%20clean%20background%2C%20warm%20natural%20light&width=600&height=400&seq=proyecto-civil-001&orientation=landscape',
  },
  {
    icon: 'ri-water-flash-line',
    titulo: 'Hidráulica y Sanitaria',
    descripcion:
      'Diseño y construcción de sistemas de agua potable, alcantarillado, plantas de tratamiento y obras hidráulicas para comunidades e industrias.',
    imagen:
      'https://readdy.ai/api/search-image?query=hydraulic%20infrastructure%20water%20treatment%20plant%20pipes%20and%20pumping%20systems%2C%20industrial%20construction%20site%20Mexico%2C%20professional%20engineering%20photography%2C%20clean%20modern%20aesthetic%2C%20warm%20lighting&width=600&height=400&seq=proyecto-hidraulico-001&orientation=landscape',
  },
];

function AnimatedCounter({ target, prefix }: { target: number; prefix: string }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, target]);

  return (
    <span ref={ref} className="text-4xl font-bold text-gray-900">
      {prefix}{count}
    </span>
  );
}

export default function Proyectos() {
  return (
    <section id="proyectos" className="py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Encabezado */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-600 text-xs font-semibold tracking-widest uppercase rounded-full mb-4">
            Nuestros Proyectos
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
            Experiencia que <span className="text-teal-500">respalda</span> cada obra
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            A lo largo de más de dos décadas hemos desarrollado proyectos de alto impacto en distintas regiones del país, consolidando nuestra presencia en el sector de la infraestructura.
          </p>
          <div className="w-16 h-1 bg-teal-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {proyectos.map((item) => (
            <div
              key={item.label}
              className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-4">
                <i className={`${item.icon} text-2xl text-teal-500`}></i>
              </div>
              <p className="mb-1">
                <AnimatedCounter target={item.numero} prefix={item.prefix} />
              </p>
              <p className="text-sm text-gray-500 font-medium">{item.label}</p>
            </div>
          ))}
        </div>

        {/* Categorías de proyectos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {categorias.map((cat) => (
            <div
              key={cat.titulo}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group cursor-default"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={cat.imagen}
                  alt={cat.titulo}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg">
                    <i className={`${cat.icon} text-white text-base`}></i>
                  </div>
                  <h3 className="text-white font-bold text-base">{cat.titulo}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-500 text-sm leading-relaxed">{cat.descripcion}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
